import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation, type TranslatedString } from "@i18n";
import { showToastNotification } from "@Reducers/toastSlice";
import { setPortalChatUnreadCount } from "@Reducers/portalChat";
import {
  getConversationsRequest,
  getMessagesRequest,
  sendMessageRequest,
  editMessageRequest,
  deleteMessageRequest,
  toggleReactionRequest,
  markConversationReadRequest,
  updateConversationStateRequest,
  deleteAttachmentRequest,
  getUnreadSummaryRequest,
  type ChatConversation,
  type ChatSection,
  type ChatMessage,
} from "../api/portalChat.api";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChatSocket } from "../hooks/usePortalChatSocket";
import {
  SCOPE_STORAGE_KEY,
  LAST_CONVERSATION_STORAGE_KEY,
  TYPING_TTL_MS,
  TYPING_SWEEP_INTERVAL_MS,
  BADGE_REFRESH_DEBOUNCE_MS,
  PREVIEW_LENGTH,
  UNREAD_COUNT_CAP,
  THREAD_WINDOW_SIZE,
  translateServerError,
} from "../portalChat.constants";
import type { PortalChatContextValue, ThreadData, ComposerIntent, ChatScope, FailedSend } from "../components/context";
import type { PortalChatPlatform } from "./platform";

/**
 * The whole Portal Chat brain, shared verbatim by the web panel (PortalChatPanel) and the mobile
 * screens (packages/mobile/src/Components/PortalChat) — list state, open thread, socket handlers,
 * unread badge, resurface rules, typing TTLs, reconnect resync. Everything platform-specific is
 * behind the `PortalChatPlatform` adapter; the platform shells only render.
 *
 * Redux is shared too: the toast slice and the portalChat badge slice are registered in BOTH
 * stores (web reducers/index.ts, mobile rootReducer.ts), so the core dispatches them directly.
 */
export function usePortalChatCore({
  show,
  userId,
  userName,
  currentProjectId,
  platform,
  sessionKey,
}: {
  /** Web: the panel is open. Mobile: a chat screen is focused. Gates list upkeep vs badge-only. */
  show: boolean;
  userId: string;
  userName: string;
  /** The project the user is working in right now (web route / mobile param), or null. */
  currentProjectId: string | null;
  platform: PortalChatPlatform;
  /**
   * Identity of the chat session (mobile: portal id). Changing it resets EVERY piece of chat
   * state and reconnects the socket with fresh credentials — without unmounting anything, so
   * navigation state survives a portal switch. Web omits it (portal switches are full page loads).
   */
  sessionKey?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [scope, setScopeState] = useState<ChatScope>(() => {
    // PRD §4.7: the last picked scope persists per tab/device (the open-edge latch below overrides
    // to Project Chats when chat is opened from inside a project).
    const stored = platform.storage.getItem(SCOPE_STORAGE_KEY);
    if (stored === "all" || stored === "direct" || stored === "group" || stored === "project") return stored;
    return "all";
  });
  /**
   * Armed while an open is still waiting for its first conversations list; the first list that is
   * asked to restore consumes it. Makes PRD §4.1 restore a once-per-OPEN privilege, so a later
   * list (socket-driven add/removal, a dialog refresh) can never re-open the id still parked in
   * storage after a deliberate deselect and silently mark it read.
   * ⚠️ It is re-armed in TWO places — the close edge below and the portal-switch reset — and web
   * only ever reaches the first: web passes no `sessionKey`, so the portal-switch effect
   * early-returns forever there.
   */
  const restoreArmedRef = useRef(true);
  /**
   * PRD §4.7: opening from inside a project lands on Project Chats, overriding the last picked
   * scope — but WITHOUT overwriting the persisted preference (setScopeState, not setScope).
   * ⚠️ The override belongs to the open EDGE only, which is why it latches here instead of riding
   * the open effect: that effect also re-runs whenever `refreshList`/`selectConversation` change
   * identity (`t` changes on a language switch), and re-applying the override there drags the user
   * back to Project Chats after they picked another scope.
   */
  const [prevShow, setPrevShow] = useState(show);
  if (!Object.is(prevShow, show)) {
    setPrevShow(show);
    if (show && platform.forceProjectScopeOnOpen && currentProjectId) setScopeState("project");
    // Without this re-arm, restore degrades to once per PAGE LOAD on web: after any deselect
    // (hiding the selected chat runs selectNextAfter → selectConversation(null)), closing and
    // reopening the panel would land on the empty state for the rest of the page's life.
    if (!show) restoreArmedRef.current = true;
  }
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [sections, setSections] = useState<ChatSection[]>([]);
  const [projectOrder, setProjectOrder] = useState<string[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  /** True once a conversations list for THIS portal session has arrived (reset on portal switch). */
  const [listLoaded, setListLoaded] = useState(false);
  const [connected, setConnected] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [composerIntent, setComposerIntent] = useState<ComposerIntent>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const [typingByConversation, setTypingByConversation] = useState<Record<string, Record<string, { until: number }>>>(
    {},
  );
  const [forwardMessage, setForwardMessage] = useState<ChatMessage | null>(null);
  const [receiptsMessage, setReceiptsMessage] = useState<ChatMessage | null>(null);
  const [reactionsMessage, setReactionsMessage] = useState<ChatMessage | null>(null);
  const [editDialogMessage, setEditDialogMessage] = useState<ChatMessage | null>(null);
  const [failedSend, setFailedSend] = useState<FailedSend | null>(null);
  const [menuEpoch, setMenuEpoch] = useState(0);

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const threadRef = useRef<ThreadData | null>(null);
  threadRef.current = thread;
  const showRef = useRef(show);
  showRef.current = show;
  const summaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every sessionKey (portal) switch; long-flight responses from the previous portal
  // compare against it and discard themselves instead of repopulating cleared state.
  const sessionEpochRef = useRef(0);
  const prevConnectedRef = useRef(true);
  const platformRef = useRef(platform);
  platformRef.current = platform;

  const showToast = useCallback(
    (content: TranslatedString, success = true) => {
      dispatch(showToastNotification({ content, type: success ? "success" : "error" }));
    },
    [dispatch],
  );

  /** Server errors arrive as fixed English sentences — map them through i18n before toasting. */
  const showServerError = useCallback(
    (serverMessage: string | undefined, fallback: TranslatedString) =>
      showToast(translateServerError(t, serverMessage, fallback), false),
    [showToast, t],
  );

  const publishBadge = useCallback(
    (rows: ChatConversation[]) => {
      // Muted AND hidden conversations are excluded from the icon badge (PRD §4.5/§4.11).
      const count = rows.filter((c) => !c.muted && !c.hidden && c.unreadCount > 0).length;
      dispatch(setPortalChatUnreadCount(count));
    },
    [dispatch],
  );

  /** Conversations marked read very recently — a refreshList whose server snapshot predates the
   *  mark must not resurrect their unread counts. */
  const recentlyReadRef = useRef(new Map<string, number>());

  const markRead = useCallback(
    (conversationId: string) => {
      recentlyReadRef.current.set(conversationId, Date.now());
      void markConversationReadRequest({ conversationId: toObjectIdString(conversationId) }).catch((error: unknown) =>
        Sentry.captureException(error),
      );
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId ? { ...c, unreadCount: 0, lastReadDate: Date.now() } : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge],
  );

  // A message that arrives while the thread is open but NOT visible (hidden tab / backgrounded
  // app) is appended without consuming a read receipt; the read catches up the moment the thread
  // is actually seen again, so senders get their double tick and no phantom badge resurrects.
  const pendingReadRef = useRef<string | null>(null);
  useEffect(() => {
    const subscribe = platformRef.current.subscribeAppVisibility;
    if (!subscribe) return undefined;
    return subscribe(() => {
      const pending = pendingReadRef.current;
      if (pending && pending === selectedIdRef.current && platformRef.current.isAppVisible()) {
        pendingReadRef.current = null;
        markRead(pending);
      }
    });
  }, [markRead]);

  /** Monotonic per-select sequence: a slow response from an OLDER select of the SAME conversation
   *  must not clobber a newer one (the id-equality guard alone cannot see that). */
  const selectSeqRef = useRef(0);
  /** The last non-null selection — an armed reply intent survives transient deselects and
   *  re-selects of the SAME conversation (mobile camera round-trip), clears on real switches. */
  const lastSelectedRef = useRef<string | null>(null);

  const selectConversation = useCallback(
    (conversationId: string | null) => {
      pendingReadRef.current = null;
      selectSeqRef.current += 1;
      const seq = selectSeqRef.current;
      const isNewConversation = conversationId !== null && conversationId !== lastSelectedRef.current;
      if (conversationId !== null) lastSelectedRef.current = conversationId;
      setSelectedId(conversationId);
      if (isNewConversation) setComposerIntent(null);
      setHighlightMessageId(null);
      if (conversationId) platformRef.current.storage.setItem(LAST_CONVERSATION_STORAGE_KEY, conversationId);
      if (!conversationId) {
        setThread(null);
        return;
      }
      setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: true });
      void getMessagesRequest({ conversationId: toObjectIdString(conversationId) })
        .then((response) => {
          if (selectedIdRef.current !== conversationId || seq !== selectSeqRef.current) return;
          if (response.data.status) {
            const fetched = response.data.result ?? [];
            const memberReadDates = (response.data.memberReadDates ?? []).map((r) => ({
              userId: String(r.userId),
              lastReadDate: r.lastReadDate,
            }));
            const members = response.data.members ?? [];
            const hasMore = response.data.hasMore ?? false;
            // Merge, don't replace: a socket message that landed while this fetch was in flight
            // was already appended to the placeholder thread and may be NEWER than the response's
            // window — a wholesale replace would silently drop it until the next re-select.
            setThread((prev) => {
              const fetchedIds = new Set(fetched.map((m) => m._id));
              const newest = fetched.at(-1)?.createdDate ?? 0;
              const liveTail = (prev?.messages ?? []).filter((m) => !fetchedIds.has(m._id) && m.createdDate >= newest);
              return { messages: [...fetched, ...liveTail], members, memberReadDates, hasMore, loading: false };
            });
            // Same visibility rule as live messages: a reconnect-driven refetch in a hidden
            // tab/backgrounded app must not consume read receipts; the subscription catches up.
            if (platformRef.current.isAppVisible()) markRead(conversationId);
            else pendingReadRef.current = conversationId;
          } else {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          if (selectedIdRef.current === conversationId && seq === selectSeqRef.current) {
            setThread({ messages: [], members: [], memberReadDates: [], hasMore: false, loading: false, error: true });
          }
        });
    },
    [markRead],
  );

  /**
   * The list fetch owns the list spinner: `withSpinner` is opt-in because only the paths that
   * REBUILD the list (opening chat, a portal switch) may blank it — a socket-driven refresh must
   * leave the rendered rows alone.
   * ⚠️ BUG (pre-existing, needs a ticket): the spinner clear is not epoch-guarded, so a fetch
   * started under the previous portal session clears the spinner after a sessionKey switch has
   * already emptied `conversations` — the list renders "no chats" until the new fetch lands.
   * Fix: clear only while `epoch === sessionEpochRef.current`.
   */
  const refreshList = useCallback(
    async (options?: { readonly withSpinner?: boolean; readonly restoreLastConversation?: boolean }) => {
      const epoch = sessionEpochRef.current;
      if (options?.withSpinner) setLoadingList(true);
      try {
        const response = await getConversationsRequest();
        if (epoch !== sessionEpochRef.current) return;
        if (response.data.status) {
          const nowMs = Date.now();
          for (const [id, at] of recentlyReadRef.current) {
            if (nowMs - at > 15000) recentlyReadRef.current.delete(id);
          }
          const rows = (response.data.result ?? []).map((c) =>
            recentlyReadRef.current.has(c._id) ? { ...c, unreadCount: 0 } : c,
          );
          setConversations(rows);
          setSections(response.data.sections ?? []);
          setProjectOrder((response.data.projectOrder ?? []).map(String));
          setListLoaded(true);
          publishBadge(rows);
          // PRD §4.1: the Chats icon opens the last conversation, or the empty state if none exists.
          // Mobile opts out (restoreLastConversationOnOpen: false): its navigation stack restores
          // itself, and a background auto-select would consume read receipts for messages the user
          // never saw.
          // ⚠️ Restore is opt-in because it must not ride a caller's fetch: every other caller
          // refreshes a list the user is already looking at, and `startDirectWith`
          // (core/useChatListModel.ts) awaits this call and then selects the chat it just created
          // — restoring here would fire a getMessages for the parked id and rewrite the stored id
          // to a stale one an instant before that select lands.
          // ⚠️ `showRef`, not a captured `show`: the panel can be closed by the time the response
          // arrives, and restoring into a closed panel consumes read receipts for messages nobody
          // has seen.
          if (options?.restoreLastConversation && restoreArmedRef.current) {
            restoreArmedRef.current = false;
            if (showRef.current && !selectedIdRef.current && platformRef.current.restoreLastConversationOnOpen) {
              const lastId = platformRef.current.storage.getItem(LAST_CONVERSATION_STORAGE_KEY);
              if (lastId && rows.some((c) => c._id === lastId && !c.hidden)) selectConversation(lastId);
            }
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        showToast(t("Could not load chats"), false);
      } finally {
        if (options?.withSpinner) setLoadingList(false);
      }
    },
    [publishBadge, selectConversation, showToast, t],
  );

  // Open/close transitions (placed after refreshList/selectConversation so the deps don't hit their TDZ).
  // ⚠️ Both hosts keep this hook mounted while chat is CLOSED (web renders null after calling it,
  // mobile's provider wraps the whole app) so the badge keeps updating — the `show` gate is what
  // separates list upkeep from badge-only. Moving the gate out to a conditionally mounted child
  // takes BOTH hosts at once (web PortalChatPanel and packages/mobile/src/Components/PortalChat/
  // PortalChatProvider.tsx, whose element chain around {children} must not change shape); a
  // one-sided move silently stops list upkeep on the other platform.
  // ⚠️ This effect also re-runs on a `refreshList`/`selectConversation` identity change (`t` changes
  // on a language switch), so everything in it must be safe to repeat mid-session. Open-EDGE
  // decisions (the forced Project scope) belong to the latch above, not here.
  // ⚠️ Known React-rule debt that MUST NOT be chased by moving code: the `if (!show)` gate
  // (no-event-handler), the spinner this open raises through `withSpinner`
  // (no-adjust-state-on-prop-change on `loadingList`) and the restore that now runs inside
  // `refreshList` are all still the same violation — hoisting a guard out of an effect callback
  // only moves it past the linter. The single real fix is the both-hosts child mount above.
  useEffect(() => {
    // eslint-disable-next-line react-effect/no-event-handler -- unfixable here; see the adjacent note
    if (!show) return;
    void refreshList({ withSpinner: true, restoreLastConversation: true });
    // A selection retained from before chat was closed may be stale — messages that arrived
    // while closed were never appended (closed-chat events only refresh the badge). Refetch it.
    if (selectedIdRef.current) selectConversation(selectedIdRef.current);
  }, [show, refreshList, selectConversation]);

  /** Append a live/sent message, keeping at most THREAD_WINDOW_SIZE rows mounted — older
   *  rows re-page through loadOlderMessages, so hasMore flips back on when the window trims. */
  const appendToThread = (prev: ThreadData | null, message: ChatMessage): ThreadData | null => {
    if (!prev || prev.messages.some((m) => m._id === message._id)) return prev;
    const messages = [...prev.messages, message];
    if (messages.length <= THREAD_WINDOW_SIZE) return { ...prev, messages };
    return { ...prev, messages: messages.slice(messages.length - THREAD_WINDOW_SIZE), hasMore: true };
  };

  const loadOlderMessages = useCallback(async () => {
    const conversationId = selectedIdRef.current;
    if (!conversationId) return;
    const before = threadRef.current?.messages[0]?.createdDate;
    const beforeId = threadRef.current?.messages[0]?._id;
    if (!before) return;
    const response = await getMessagesRequest({
      conversationId: toObjectIdString(conversationId),
      before,
      ...(beforeId ? { beforeId: toObjectIdString(beforeId) } : {}),
    });
    if (response.data.status && selectedIdRef.current === conversationId) {
      const older = response.data.result ?? [];
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: [...older, ...prev.messages.filter((m) => !older.some((o) => o._id === m._id))],
              hasMore: response.data.status ? (response.data.hasMore ?? false) : prev.hasMore,
            }
          : prev,
      );
    }
  }, []);

  /** Chat closed: refresh just the badge, debounced — a busy space chat must not fan out
   *  one full getConversations per message per connected client. */
  const refreshBadgeDebounced = useCallback(() => {
    if (summaryTimerRef.current) return;
    summaryTimerRef.current = setTimeout(() => {
      summaryTimerRef.current = null;
      const epoch = sessionEpochRef.current;
      getUnreadSummaryRequest()
        .then((response) => {
          if (epoch !== sessionEpochRef.current) return;
          if (response.data.status) dispatch(setPortalChatUnreadCount(response.data.unreadConversations ?? 0));
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }, [dispatch]);

  const applyConversationPreview = useCallback(
    (conversationId: string, message: ChatMessage, incrementUnread: boolean) => {
      if (!showRef.current) {
        refreshBadgeDebounced();
        return;
      }
      setConversations((prev) => {
        const known = prev.some((c) => c._id === conversationId);
        if (!known) {
          // ⚠️ BUG (pre-existing, needs a ticket): a side effect inside a state updater. React may
          // invoke an updater more than once (StrictMode double-invoke, concurrent re-render), so
          // one socket message for an unknown conversation can fire two getConversations fetches.
          // Fix: read membership before the setState and call refreshList outside the updater.
          void refreshList();
          return prev;
        }
        const next = prev.map((c) => {
          if (c._id !== conversationId) return c;
          const mentioned = (message.mentions ?? []).some((m) => String(m) === userId);
          return {
            ...c,
            lastMessageDate: message.createdDate,
            lastMessage: {
              messageId: message._id,
              senderId: message.senderId,
              senderName: message.senderName,
              kind: message.kind,
              text: (message.text ?? "").slice(0, PREVIEW_LENGTH),
              hasAttachments: message.attachments?.length ? true : undefined,
              deleted: message.deleted,
            },
            unreadCount: incrementUnread ? Math.min(c.unreadCount + 1, UNREAD_COUNT_CAP) : c.unreadCount,
            // Resurface parity with the server (PRD §4.11): direct/group return on a new message
            // unless muted (mention bypasses mute); hidden PROJECT chats stay hidden unless mentioned.
            hidden:
              c.hidden &&
              (c.type === "space" ? !mentioned : (c.muted && !mentioned) || String(message.senderId) === userId)
                ? c.hidden
                : false,
          };
        });
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, refreshBadgeDebounced, refreshList, userId],
  );

  const { emitTyping: socketEmitTyping, reconnectNow } = usePortalChatSocket(
    Boolean(userId),
    {
      // Messages sent while the socket was down were only ever broadcast live — on the
      // disconnected→connected transition, refetch so the thread has no silent hole. The
      // transition is read from the socket's own argument, not from `connected`: a
      // disconnect/reconnect pair that lands inside one render batch leaves that state
      // unchanged, and a resync keyed on it would never run.
      // ⚠️ INVARIANT this resync depends on: usePortalChatSocket keeps this handler object in a ref
      // it reassigns during render and every `socket.on` reads `eventsRef.current`, so the closures
      // below (`refreshList`, `selectConversation`, `refreshBadgeDebounced`) are always the current
      // render's. Its connect effect deps are only [enabled, reconnectKey] — if anyone ever captures
      // `events` inside that effect instead, this resync silently freezes on mount-time closures and
      // a reconnect after a language switch refetches through a stale `refreshList`.
      onConnectionChange: (isConnected) => {
        setConnected(isConnected);
        const was = prevConnectedRef.current;
        prevConnectedRef.current = isConnected;
        if (was || !isConnected) return;
        if (showRef.current) {
          void refreshList();
          if (selectedIdRef.current) selectConversation(selectedIdRef.current);
        } else {
          refreshBadgeDebounced();
        }
      },
      onMessageNew: ({ conversationId, message }) => {
        const own = String(message.senderId) === userId;
        const isOpen = selectedIdRef.current === conversationId && show;
        applyConversationPreview(conversationId, message, !own && !isOpen);
        if (isOpen) {
          setThread((prev) => appendToThread(prev, message));
          // Only an actually-visible thread counts as read — a background tab/app must not
          // consume read receipts for messages the user never saw. The read catches up via the
          // visibility subscription above once the thread is seen again.
          if (!own) {
            if (platformRef.current.isAppVisible()) markRead(conversationId);
            else pendingReadRef.current = conversationId;
          }
        }
        setTypingByConversation((prev) => {
          const forConversation = { ...(prev[conversationId] ?? {}) };
          delete forConversation[String(message.senderId)];
          return { ...prev, [conversationId]: forConversation };
        });
      },
      onMessageEdited: ({ conversationId, message }) => {
        if (selectedIdRef.current === conversationId) {
          setThread((prev) =>
            prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
          );
        }
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversationId && c.lastMessage?.messageId === message._id
              ? { ...c, lastMessage: { ...c.lastMessage, text: (message.text ?? "").slice(0, PREVIEW_LENGTH) } }
              : c,
          ),
        );
      },
      onMessageDeleted: ({ conversationId, messageId }) => {
        if (selectedIdRef.current === conversationId) {
          setThread((prev) =>
            prev
              ? {
                  ...prev,
                  messages: prev.messages.map((m) =>
                    m._id === messageId
                      ? {
                          ...m,
                          deleted: true,
                          text: undefined,
                          attachments: undefined,
                          reactions: undefined,
                          replyTo: undefined,
                          forwardOf: undefined,
                        }
                      : m,
                  ),
                }
              : prev,
          );
        }
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversationId && c.lastMessage?.messageId === messageId
              ? { ...c, lastMessage: { ...c.lastMessage, text: "", deleted: true } }
              : c,
          ),
        );
      },
      onReactionUpdated: ({ conversationId, messageId, reactions }) => {
        if (selectedIdRef.current !== conversationId) return;
        const branded = reactions.map((reaction) => ({
          emoji: reaction.emoji,
          users: reaction.users.map((id) => toObjectIdString(id)),
        }));
        setThread((prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions: branded } : m)),
              }
            : prev,
        );
      },
      onReadUpdated: ({ conversationId, userId: readerId, lastReadDate }) => {
        if (selectedIdRef.current !== conversationId) return;
        setThread((prev) =>
          prev
            ? {
                ...prev,
                memberReadDates: [
                  ...prev.memberReadDates.filter((r) => r.userId !== readerId),
                  { userId: readerId, lastReadDate },
                ],
              }
            : prev,
        );
      },
      onConversationUpdated: () => {
        if (!showRef.current) {
          refreshBadgeDebounced();
          return;
        }
        void refreshList();
        const conversationId = selectedIdRef.current;
        if (conversationId) {
          // Membership / name may have changed; refresh the open thread's members lazily.
          void getMessagesRequest({ conversationId: toObjectIdString(conversationId), limit: 1 }).then((response) => {
            if (response.data.status && selectedIdRef.current === conversationId) {
              setThread((prev) =>
                prev ? { ...prev, members: response.data.status ? (response.data.members ?? []) : prev.members } : prev,
              );
            }
          });
        }
      },
      onMemberRemoved: ({ conversationId }) => {
        if (!showRef.current) {
          // The list is empty/stale while closed — let the debounced summary recompute the badge.
          refreshBadgeDebounced();
          return;
        }
        // PRD §4.3: the conversation disappears immediately for the removed member, even if open.
        // Web's two-pane auto-selects the next conversation; mobile deselects instead — selecting
        // would invisibly mark the fallback read while the user is on a different screen.
        // ⚠️ BUG (pre-existing, needs a ticket): the `setTimeout` below is a side effect inside a
        // state updater — a double-invoked updater schedules two selectConversation calls, i.e. two
        // getMessages fetches and two markRead round-trips for the fallback. Fix: compute the
        // fallback from the current list before the setState and select outside the updater.
        setConversations((prev) => {
          const next = prev.filter((c) => c._id !== conversationId);
          publishBadge(next);
          if (selectedIdRef.current === conversationId) {
            const fallback = platformRef.current.autoSelectFallbackOnRemoval ? next.find((c) => !c.hidden) : undefined;
            setTimeout(() => selectConversation(fallback ? fallback._id : null), 0);
          }
          return next;
        });
      },
      onTyping: ({ conversationId, userId: typerId, isTyping }) => {
        setTypingByConversation((prev) => {
          const forConversation = { ...(prev[conversationId] ?? {}) };
          if (isTyping) forConversation[typerId] = { until: Date.now() + TYPING_TTL_MS };
          else delete forConversation[typerId];
          return { ...prev, [conversationId]: forConversation };
        });
      },
    },
    { getConnectOptions: platform.getSocketOptions, reconnectKey: sessionKey },
  );

  // The badge debounce survives renders in a ref; clear it when the host unmounts (web panel
  // never unmounts, but the mobile CoreHost does on logout/portal switch).
  useEffect(
    () => () => {
      if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
    },
    [],
  );

  // Portal switch (mobile): drop every piece of per-portal state in place. The socket hook
  // reconnects on its own via reconnectKey; nothing unmounts, so navigation state survives.
  // ⚠️ The whole switch must stay in ONE effect: the resets, the epoch bump and the re-select are
  // a single atomic step. Splitting the state half into a render-phase latch gives the transition
  // two independent guards (a latch key and this ref) that desync on an A→""→A flip of the portal
  // id across two renders — the latch wipes every field while the effect early-returns, leaving an
  // empty list under a spinner nothing clears. It also splits the reset and the re-select into two
  // commits, so a cross-portal push tap renders a frame with selectedId null. A latch also runs
  // BEFORE the effect it would feed, so `selectedIdRef.current` is already null by the time
  // `retainedSelection` reads it — that selection is then lost outright, not merely delayed.
  // ⚠️ The React-idiomatic reset is a `key` on whatever owns this state, and only the mobile host
  // can pass one (web never sends sessionKey). It is not a free refactor: remounting drops
  // `retainedSelection`, so the cross-portal push tap needs a product answer first.
  const prevSessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    // eslint-disable-next-line react-effect/no-event-handler -- unfixable here; see the adjacent note
    if (prevSessionKeyRef.current === sessionKey) return;
    prevSessionKeyRef.current = sessionKey;
    sessionEpochRef.current += 1;
    // A cross-portal push tap navigates to the thread BEFORE the new portal's data resolves and
    // flips sessionKey — that selection is valid in the NEW portal and must survive the reset.
    // A drawer-initiated switch has no chat screen focused (show false), so nothing re-selects.
    const retainedSelection = showRef.current ? selectedIdRef.current : null;
    // ⚠️ These setters are ONE atomic per-portal wipe: every field is portal-scoped, so leaving any one
    // behind paints the old portal's data under the new portal's heading — a conversation row, a thread, or an
    // open forward/receipts/reactions/edit dialog addressing a message the new portal cannot resolve.
    // `listLoaded` and `unreadOnly` belong to the same wipe: a retained `listLoaded` renders the empty-state
    // "no chats" for the new portal before its first fetch lands, and a retained `unreadOnly` silently filters
    // the new list by the previous portal's toggle.
    // Adjusting any of them during render is not available here — see the atomicity trap above: a latch runs
    // before this effect, desyncs from `prevSessionKeyRef`, and splits the wipe from the re-select. The real
    // fix is a `key` passed by the mobile host (packages/mobile/src/Components/PortalChat/), and that needs
    // the `retainedSelection` product answer first.
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setConversations([]);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setSections([]);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setProjectOrder([]);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setSelectedId(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setThread(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setComposerIntent(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setHighlightMessageId(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setTypingByConversation({});
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setForwardMessage(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setReceiptsMessage(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setReactionsMessage(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setEditDialogMessage(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setFailedSend(null);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setUnreadOnly(false);
    // eslint-disable-next-line react-effect/no-adjust-state-on-prop-change -- unfixable here; see the adjacent note
    setListLoaded(false);
    restoreArmedRef.current = true;
    pendingReadRef.current = null;
    recentlyReadRef.current.clear();
    dispatch(setPortalChatUnreadCount(0));
    if (showRef.current) {
      void refreshList({ withSpinner: true });
      if (retainedSelection) selectConversation(retainedSelection);
    }
    // prevSessionKeyRef guards re-entry, so extra identity-driven re-runs are no-ops.
  }, [sessionKey, dispatch, refreshList, selectConversation]);

  // Sweep expired typing entries.
  useEffect(() => {
    const timer = setInterval(() => {
      setTypingByConversation((prev) => {
        const nowMs = Date.now();
        let changed = false;
        const next: typeof prev = {};
        for (const [conversationId, users] of Object.entries(prev)) {
          const alive = Object.fromEntries(Object.entries(users).filter(([, v]) => v.until > nowMs));
          if (Object.keys(alive).length !== Object.keys(users).length) changed = true;
          next[conversationId] = alive;
        }
        return changed ? next : prev;
      });
    }, TYPING_SWEEP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const sendMessage = useCallback<PortalChatContextValue["sendMessage"]>(
    async ({ text, attachments, replyToMessageId, forwardOfMessageId, mentions, conversationId: explicitId }) => {
      const conversationId = explicitId ?? selectedIdRef.current;
      if (!conversationId) return false;
      try {
        const response = await sendMessageRequest({
          conversationId: toObjectIdString(conversationId),
          ...(text ? { text } : {}),
          ...(attachments?.length ? { attachments } : {}),
          ...(replyToMessageId ? { replyToMessageId: toObjectIdString(replyToMessageId) } : {}),
          ...(forwardOfMessageId ? { forwardOfMessageId: toObjectIdString(forwardOfMessageId) } : {}),
          ...(mentions?.length ? { mentions: mentions.map((id) => toObjectIdString(id)) } : {}),
        });
        if (!response.data.status) {
          showServerError(response.data.errorMessage, t("Could not send the message"));
          return false;
        }
        const message = response.data.result;
        if (message) {
          // Guarded like every other async path: a reply that lands after the user switched
          // conversations must not append into the newly opened thread.
          if (selectedIdRef.current === conversationId) {
            setThread((prev) => appendToThread(prev, message));
          }
          applyConversationPreview(conversationId, message, false);
        }
        return true;
      } catch (error) {
        Sentry.captureException(error);
        showToast(t("Could not send the message"), false);
        return false;
      }
    },
    [applyConversationPreview, showServerError, showToast, t],
  );

  const editMessage = useCallback(
    async (messageId: string, text: string) => {
      const response = await editMessageRequest({ messageId: toObjectIdString(messageId), text });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not edit the message"));
        return false;
      }
      const message = response.data.result;
      if (message) {
        setThread((prev) =>
          prev ? { ...prev, messages: prev.messages.map((m) => (m._id === message._id ? message : m)) } : prev,
        );
      }
      showToast(t("Message edited."));
      return true;
    },
    [showServerError, showToast, t],
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      const response = await deleteMessageRequest({ messageId: toObjectIdString(messageId) });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Could not delete the message"));
        return;
      }
      setThread((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.map((m) =>
                m._id === messageId
                  ? {
                      ...m,
                      deleted: true,
                      text: undefined,
                      attachments: undefined,
                      reactions: undefined,
                      replyTo: undefined,
                      forwardOf: undefined,
                    }
                  : m,
              ),
            }
          : prev,
      );
    },
    [showServerError, t],
  );

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    const response = await toggleReactionRequest({ messageId: toObjectIdString(messageId), emoji });
    if (response.data.status) {
      const reactions = response.data.reactions ?? [];
      setThread((prev) =>
        prev ? { ...prev, messages: prev.messages.map((m) => (m._id === messageId ? { ...m, reactions } : m)) } : prev,
      );
    }
  }, []);

  const updateConversationState = useCallback(
    async (conversationId: string, patch: { hidden?: boolean; muted?: boolean; sectionId?: string | null }) => {
      const response = await updateConversationStateRequest({
        conversationId: toObjectIdString(conversationId),
        hidden: patch.hidden,
        muted: patch.muted,
        ...(patch.sectionId !== undefined
          ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
          : {}),
      });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
        return;
      }
      setConversations((prev) => {
        const next = prev.map((c) =>
          c._id === conversationId
            ? {
                ...c,
                ...(patch.hidden !== undefined ? { hidden: patch.hidden } : {}),
                ...(patch.muted !== undefined ? { muted: patch.muted } : {}),
                ...(patch.sectionId !== undefined
                  ? { sectionId: patch.sectionId === null ? null : toObjectIdString(patch.sectionId) }
                  : {}),
              }
            : c,
        );
        publishBadge(next);
        return next;
      });
    },
    [publishBadge, showServerError, t],
  );

  const deleteAttachment = useCallback(
    async (messageId: string, awsKey: string) => {
      const response = await deleteAttachmentRequest({ messageId: toObjectIdString(messageId), awsKey });
      if (!response.data.status) {
        showServerError(response.data.errorMessage, t("Something went wrong"));
      }
      // The message:edited (or message:deleted) socket echo carries the updated state.
    },
    [showServerError, t],
  );

  const unhideAndOpen = useCallback(
    (conversationId: string) => {
      const conversation = conversations.find((c) => c._id === conversationId);
      if (conversation?.hidden) {
        void updateConversationState(conversationId, { hidden: false });
        showToast(t("{{name}} is back in your list.", { name: conversation.name }));
      }
      selectConversation(conversationId);
    },
    [conversations, selectConversation, showToast, t, updateConversationState],
  );

  /**
   * After `conversationId` leaves the list (hide/removal): open the next visible chat of the SAME
   * type, most recent first — under the unread filter only an unread one qualifies — or nothing,
   * which lets the thread pane show its empty state instead of yanking the user across types.
   */
  const selectNextAfter = useCallback(
    (conversationId: string) => {
      const leaving = conversations.find((c) => c._id === conversationId);
      const candidates = conversations
        .filter((c) => c._id !== conversationId && !c.hidden && (leaving === undefined || c.type === leaving.type))
        .sort((a, b) => (b.lastMessageDate ?? b.createdDate) - (a.lastMessageDate ?? a.createdDate));
      const fallback = unreadOnly ? candidates.find((c) => c.unreadCount > 0) : candidates[0];
      selectConversation(fallback ? fallback._id : null);
    },
    [conversations, selectConversation, unreadOnly],
  );

  const jumpToMessage = useCallback(
    (conversationId: string, messageId: string) => {
      // A forward source may have left the viewer's list since the page was rendered
      // (space deleted, project archived, membership lost) — never navigate into a void.
      if (!conversations.some((c) => c._id === conversationId)) {
        showToast(t("This conversation is no longer available"), false);
        return;
      }
      if (selectedIdRef.current !== conversationId) selectConversation(conversationId);
      setHighlightMessageId(messageId);
    },
    [conversations, selectConversation, showToast, t],
  );

  const emitTyping = useCallback(
    (conversationId: string, isTyping: boolean) => socketEmitTyping(conversationId, isTyping),
    [socketEmitTyping],
  );

  const typingForSelected = selectedId ? typingByConversation[selectedId] : undefined;
  const threadMembers = thread?.members;
  // TTL expiry is owned by the sweep effect, which prunes `typingByConversation` on its interval.
  const typingUsers = useMemo(() => {
    const users = typingForSelected ?? {};
    return Object.entries(users)
      .filter(([id]) => id !== userId)
      .map(([id]) => ({
        userId: id,
        // Display names come from the trusted member list, never from the socket payload.
        userName: threadMembers?.find((member) => String(member._id) === id)?.fullName ?? t("Someone"),
      }));
  }, [typingForSelected, userId, threadMembers, t]);

  const setScope = useCallback((next: ChatScope) => {
    setScopeState(next);
    platformRef.current.storage.setItem(SCOPE_STORAGE_KEY, next);
  }, []);

  const toggleUnreadOnly = useCallback(() => setUnreadOnly((prev) => !prev), []);

  /** PRD §4.6: shells bump this on layout changes (web: maximise/restore) so open menus close. */
  const bumpMenuEpoch = useCallback(() => setMenuEpoch((prev) => prev + 1), []);

  const contextValue: PortalChatContextValue = {
    userId,
    userName,
    conversations,
    sections,
    projectOrder,
    loadingList,
    listLoaded,
    connected,
    selectedId,
    thread,
    typingUsers,
    composerIntent,
    highlightMessageId,
    currentProjectId,
    scope,
    setScope,
    unreadOnly,
    toggleUnreadOnly,
    selectConversation,
    refreshList,
    loadOlderMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    deleteAttachment,
    toggleReaction,
    setComposerIntent,
    setHighlightMessageId,
    updateConversationState,
    emitTyping,
    markConversationRead: markRead,
    openForwardDialog: setForwardMessage,
    openReceiptsDialog: setReceiptsMessage,
    openReactionsDialog: setReactionsMessage,
    openEditDialog: setEditDialogMessage,
    unhideAndOpen,
    selectNextAfter,
    menuEpoch,
    jumpToMessage,
    failedSend,
    setFailedSend,
    showToast,
    showServerError,
  };

  return {
    contextValue,
    connected,
    /** Dialog-open state lives in the core so both shells drive the same open/close contract. */
    dialogs: {
      forwardMessage,
      receiptsMessage,
      reactionsMessage,
      editDialogMessage,
      closeForward: () => setForwardMessage(null),
      closeReceipts: () => setReceiptsMessage(null),
      closeReactions: () => setReactionsMessage(null),
      closeEdit: () => setEditDialogMessage(null),
    },
    bumpMenuEpoch,
    /** Mobile: nudge a stalled socket on AppState→active / NetInfo regain. */
    reconnectNow,
  };
}
